import map from "lodash/map";
import { ProfileItem } from "PFApp/components/profile_item";
import { useGrowlStoreActions } from "PFApp/stores/growl_store";
import { Stack } from "PFComponents/containers";
import { Modal } from "PFComponents/modal";
import { SelectV2 } from "PFComponents/select_v2";
import { Typography } from "PFComponents/typography";
import { getProfileName } from "PFCore/helpers/profile";
import { useActivityEconomicsScenarioShare } from "PFCore/hooks/queries/activity/use_activity_economics_scenario_share";
import { useCurrentProfile } from "PFCore/hooks/queries/profile/use_current_profile";
import { fetchProfilesOptions } from "PFCore/services/profile";
import { BasicProfile, Id } from "PFTypes";
import { Option } from "PFTypes/options";
import { useState } from "react";
import { useTranslation } from "react-i18next";

type ShareScenarioModalProps = {
  scenarioId: Id;
  onClose: () => void;
};

export const ShareScenarioModal = ({ scenarioId, onClose }: ShareScenarioModalProps) => {
  const { t } = useTranslation("activities", { keyPrefix: "show.economics.shareScenarioModal" });
  const { showGrowl } = useGrowlStoreActions();

  const { data: currentProfile } = useCurrentProfile();
  const { mutateAsync: shareScenario } = useActivityEconomicsScenarioShare(scenarioId);

  const [profiles, setProfiles] = useState<BasicProfile[]>([]);

  const handleShare = async () => {
    await shareScenario(map(profiles, "id"), {
      onSuccess: () => {
        showGrowl({ message: t("shareSuccess"), kind: "success" });
        onClose?.();
      },
      onError: () => {
        showGrowl({ message: t("shareError"), kind: "error" });
      }
    });
  };

  return (
    <Modal
      title={t("title")}
      onOK={handleShare}
      onClose={onClose}
      labelOK={t("okLabel")}
      disableOKButton={!profiles.length}
    >
      <Stack>
        <SelectV2<BasicProfile>
          label={t("label")}
          fetchOptions={async ({ searchTerm, ids }) => {
            const result = await fetchProfilesOptions({
              term: searchTerm,
              value: ids
            });
            return {
              entries: result.entries.map((item) => ({
                id: item.id,
                displayElement: <ProfileItem profile={item} />,
                value: getProfileName(item),
                original: item
              })),
              meta: result.meta
            };
          }}
          parseOptions={(options: Option<BasicProfile>[]) =>
            options.filter((item) => currentProfile.id !== item.id)
          }
          onChange={(options: Option<BasicProfile>[]) => setProfiles(options.map(({ original }) => original))}
          onClear={profiles.length > 0 ? () => setProfiles([]) : undefined}
          value={profiles.map(({ id }) => id)}
          dropdownId="share-scenario-modal"
          multiple
          isFetchingSelectedOptions={false}
        />
        <Typography variant="bodyBold">{t("disclaimerTitle")}</Typography>
        <Typography variant="bodyRegular">{t("disclaimer")}</Typography>
      </Stack>
    </Modal>
  );
};
